/**
 * Woran es hakt — die gelben Kästen über einer Ansicht.
 *
 * **Jeder trägt den Weg dorthin, wo sich etwas tun lässt**, und keiner behauptet
 * mehr, als er weiß: Die gemeldete Abmeldung ist ein Hinweis und keine Freigabe
 * (OQ-023), und gegen schwachen Funk gibt es hier keinen Knopf, sondern einen
 * Satz. Was hier steht, rechnet `deviceHints` in `lib/device-report.ts` aus.
 *
 * **Seit HM-056 steht die Liste nicht mehr nur auf der Detailseite**, sondern
 * auch über der Geräteverwaltung: Ein Ausfall der Annahmestelle betrifft alle
 * Geräte gleichzeitig, und wer ihn nur auf der Detailseite erklärt, erklärt ihn
 * genau dort nicht, wo er auffällt. `onGo` ist deshalb optional — in einer Liste
 * gibt es keine Reiter, in die ein Hinweis führen könnte.
 */
import { Button } from "@hausmeter/ui";
import type { Hint } from "../lib/device-report.js";
import { navigate, routePath, type DeviceTab } from "../lib/router.js";

export function HintList({
  hints,
  onGo,
}: {
  hints: readonly Hint[];
  onGo?: (tab: DeviceTab) => void;
}) {
  if (hints.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
      {hints.map((hint) => {
        // Vor der Schließung festgehalten: Eine Eigenschaft, die außerhalb
        // geprüft wurde, gilt dem Typprüfer innerhalb eines Rückrufs wieder als
        // möglicherweise leer.
        const go = hint.go;

        return (
          <div
            key={hint.id}
            style={{
              display: "flex",
              gap: "var(--sp-3)",
              alignItems: "flex-start",
              flexWrap: "wrap",
              padding: "12px 14px",
              borderRadius: "var(--radius-md)",
              background: "var(--tone-warning-bg)",
              border: "1px solid var(--tone-warning-border)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                flexShrink: 0,
                marginTop: 7,
                borderRadius: "50%",
                background: "var(--hm-warning)",
              }}
            />
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--hm-warning)" }}>
                {hint.title}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--text-body)" }}>
                {hint.text}
              </div>
            </div>
            {go !== undefined && hint.action !== undefined && onGo !== undefined && (
              <Button variant="secondary" size="sm" onClick={() => onGo(go)}>
                {hint.action}
              </Button>
            )}
            {/* Eine andere Adresse ist ein Ort und deshalb ein Link (HM-113) —
                Strg-, Cmd- und Mittelklick gehen an den Browser. */}
            {hint.route !== undefined && hint.action !== undefined && (
              <a
                href={routePath(hint.route)}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
                    return;
                  event.preventDefault();
                  if (hint.route !== undefined) navigate(hint.route);
                }}
                style={{
                  alignSelf: "center",
                  padding: "6px 12px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-default)",
                  boxShadow: "var(--shadow-xs)",
                  background: "var(--surface-card)",
                  color: "var(--text-strong)",
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                {hint.action}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}
